import {
  ARTIFACTS_PATH,
  type AssetType,
  type CandidateResource,
  type CandidateSlotResource,
} from '@studio/shared';
import { useCallback, useState, type ReactNode } from 'react';
import { RepeatFor } from '../../bulk/RepeatFor.js';
import { api, ApiError } from '../../shell/api.js';
import { EmptyState, Failure } from '../../shell/EmptyState.js';
import { Link, type Surface } from '../../shell/router.js';
import { useSpreadSize } from '../../shell/spread-size.js';
import { Composer, type ComposerScope } from '../Composer.js';
import { Lineage, parentOf } from '../Lineage.js';
import { SlotDetails } from '../SlotDetails.js';
import { useSlotAction } from '../useSlotAction.js';
import { useExperiment } from '../useExperiment.js';
import { ExperimentSpend } from '../Spend.js';

/**
 * An app icon and a feature graphic are judged as complete Directions and adjusted on
 * a bench; a screenshot set is read as a story and a preview video as Scenes. Explore
 * is where every Asset Type lands after `Generate`, so this is where the way on has to
 * be - a surface only an address bar can reach does not exist for the operator.
 */
const SINGLE_IMAGE: readonly AssetType[] = ['appIcon', 'featureGraphic'];

function stateLine(slot: CandidateSlotResource): string {
  switch (slot.state) {
    case 'working':
      return 'В работе…';
    case 'ready':
      return 'Готово';
    case 'failed':
      return slot.message ?? 'Этот слот остановился, ничего не произведя.';
    case 'needsAttention':
      return slot.message ?? 'Слот отправлен, исход неизвестен. Ждёт вас.';
    case 'cancelled':
      return 'Отменено';
  }
}

/**
 * The shelf of complete sets. Six slots exist from the moment `Generate` is pressed,
 * so the operator watches six places fill rather than an empty screen; a slot that
 * stopped says one line and offers the one action that helps.
 */
function Explore({ experimentId }: { experimentId: string }): ReactNode {
  const open = useExperiment(experimentId);
  const spreadSize = useSpreadSize();
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const detail = open.detail;

  const explore = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await api.spread(experimentId, 'explore');
      open.refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Ещё один набор не удалось запустить.');
    } finally {
      setBusy(false);
    }
  }, [experimentId, open]);

  if (open.error !== null) {
    return (
      <section className="page">
        <Failure message={open.error} onRetry={open.refresh} />
      </section>
    );
  }
  if (detail === null) return <section className="page" aria-busy="true" />;

  const candidates = new Map(detail.candidates.map((candidate) => [candidate.id, candidate]));
  const rows = detail.batches.flatMap((batch) => batch.slots.map((slot) => ({ batch, slot })));
  const visible = rows.filter(({ slot }) => {
    const candidate = slot.candidateId === null ? null : candidates.get(slot.candidateId);
    return candidate?.hidden !== true;
  });
  const chosen = selected === null ? null : (candidates.get(selected) ?? null);

  const scope: ComposerScope =
    chosen === null
      ? { kind: 'experiment', experimentId, brief: detail.experiment.brief }
      : { kind: 'candidate', candidateId: chosen.id, label: chosen.headline, appId: detail.experiment.appId };

  return (
    <section className="page explore">
      <div className="page-head">
        <div>
          <p className="eyebrow">{
            spreadSize === 1
              ? 'Explore · один Candidate за запуск через подписку'
              : `Explore · по ${spreadSize} готовых наборов за раз`
          }</p>
          <h1>Выбирайте историю, а не кадр.</h1>
          {/* R194: what this Experiment has cost, where the spending happens. */}
          <ExperimentSpend experimentId={experimentId} />
        </div>
        <div className="inline-actions">
          {!SINGLE_IMAGE.includes(detail.experiment.assetType) ? null : (
            <Link to={`/experiments/${experimentId}/explore/directions`} className="secondary-link">
              Открыть доску Directions
            </Link>
          )}
          {/* A preview video is read by playing it, so its shelf is the one with the
              players on it. The link is here because a surface nothing links to does
              not exist for the operator. */}
          {detail.experiment.assetType !== 'previewVideo' ? null : (
            <Link to={`/experiments/${experimentId}/video`} className="secondary-link">
              Открыть video shelf
            </Link>
          )}
          <button className="secondary" type="button" disabled={busy} onClick={() => void explore()}>
            {busy ? 'Запускаю…' : spreadSize === 1 ? 'Ещё один в Explore' : `Ещё ${spreadSize} в Explore`}
          </button>
        </div>
      </div>

      {error === null ? null : <Failure message={error} />}

      {/*
        R160: the same direction, tested somewhere else. Selecting a Candidate first
        carries it as a reference; with nothing selected the Experiment's own Brief and
        Hypothesis are what travel.
      */}
      <RepeatFor
        label="Repeat for…"
        experimentId={experimentId}
        candidateId={selected}
        note={
          selected === null
            ? 'Каждое назначение получает свой Experiment с этим Brief и Hypothesis. Сначала выберите Candidate, чтобы перенести его и как ссылку.'
            : 'Выбранный Candidate приходит в каждое назначение как креативная ссылка, не как копия, родитель или ветка.'
        }
      />

      {visible.length === 0 ? (
        <EmptyState
          title="Пока нет Candidate."
          note={
            spreadSize === 1
              ? 'Для этого Experiment ещё ничего не сгенерировано. Generate запускает один Candidate через подписку.'
              : `Для этого Experiment ещё ничего не сгенерировано. Generate сразу запускает ${spreadSize} готовых наборов, и все ${spreadSize} появляются здесь.`
          }
          action={
            <Link to={`/experiments/${experimentId}/brief`} className="primary-link">
              К Brief
            </Link>
          }
        />
      ) : (
        <ul className="candidate-shelf" aria-label="Candidates">
          {visible.map(({ slot }) => (
            <li key={slot.id} className={`candidate-row state-${slot.state}`}>
              <SlotRow
                slot={slot}
                assetType={detail.experiment.assetType}
                candidate={slot.candidateId === null ? null : (candidates.get(slot.candidateId) ?? null)}
                parent={parentOf(
                  detail.candidates,
                  slot.candidateId === null ? null : (candidates.get(slot.candidateId) ?? null),
                )}
                experimentId={experimentId}
                selected={selected === slot.candidateId && selected !== null}
                onSelect={setSelected}
                onChanged={open.refresh}
              />
            </li>
          ))}
        </ul>
      )}

      <Composer
        scope={scope}
        onDone={() => {
          setSelected(null);
          open.refresh();
        }}
      />
    </section>
  );
}

function SlotRow({
  slot,
  assetType,
  candidate,
  parent,
  experimentId,
  selected,
  onSelect,
  onChanged,
}: {
  slot: CandidateSlotResource;
  assetType: AssetType;
  candidate: CandidateResource | null;
  parent: CandidateResource | null;
  experimentId: string;
  selected: boolean;
  onSelect: (candidateId: string | null) => void;
  onChanged: () => void;
}): ReactNode {
  const { busy, failure, act } = useSlotAction(onChanged);
  const headline = candidate?.headline ?? slot.headline;
  const stopped = slot.state === 'failed' || slot.state === 'needsAttention';

  return (
    <article className={selected ? 'slot selected' : 'slot'} aria-label={headline}>
      <div className="slot-art">
        {candidate === null ? (
          <span className="frame frame-blank" aria-hidden="true" />
        ) : candidate.items.length > 0 ? (
          candidate.items.map((item) => (
            <img
              key={item.id}
              className="frame"
              src={`${ARTIFACTS_PATH}/${item.artifactId}`}
              alt={`${item.storyRole}: ${item.copy}`}
              loading="lazy"
            />
          ))
        ) : candidate.artifactId === null ? (
          <span className="frame frame-blank" aria-hidden="true" />
        ) : (
          <img className="frame" src={`${ARTIFACTS_PATH}/${candidate.artifactId}`} alt={headline} loading="lazy" />
        )}
      </div>

      <div className="slot-body">
        <h3>{headline}</h3>
        <p className="note">{`${slot.direction.marketingAngle} · ${slot.direction.visualMetaphor} · ${slot.direction.style} · ${slot.direction.composition}`}</p>
        <Lineage candidate={candidate} parent={parent} experimentId={experimentId} />
        {candidate !== null && candidate.items.length > 0 ? (
          <p className="note">
            {`${candidate.items.length} кадров по порядку: ${candidate.items.map((item) => item.storyRole).join(' → ')}`}
          </p>
        ) : null}
        {candidate !== null && candidate.items.length > 0 ? (
          <p className="note">
            {`Источники: ${[...new Set(candidate.items.map((item) => item.sourceKind))].join(', ')}`}
          </p>
        ) : null}
        <p className={stopped ? 'slot-state stopped' : 'slot-state'}>{stateLine(slot)}</p>

        <div className="row-actions">
          {candidate === null ? null : (
            <>
              <button
                type="button"
                className="ghost"
                aria-pressed={selected}
                onClick={() => onSelect(selected ? null : candidate.id)}
              >
                {`Выбрать ${headline}`}
              </button>
              <button
                type="button"
                className="ghost"
                disabled={busy}
                onClick={() =>
                  act(() => api.setCandidateFlags(candidate.id, { keep: !candidate.keep }), 'Этот Candidate не удалось Keep.')
                }
              >
                {candidate.keep ? `Kept ${headline}` : `Keep ${headline}`}
              </button>
              <button
                type="button"
                className="ghost"
                disabled={busy}
                onClick={() =>
                  act(() => api.setCandidateFlags(candidate.id, { hidden: true }), 'Этот Candidate не удалось скрыть.')
                }
              >
                {`Скрыть ${headline}`}
              </button>
              {/*
                R057 names the action, not the way in. Four descendants of this one
                Candidate, and the only way this build offered was the composer with a
                sentence typed into it - which is a different request, and a button that
                stays disabled until something is written is not this action at all.
              */}
              <button
                type="button"
                className="secondary"
                disabled={busy}
                onClick={() =>
                  act(() => api.branchCandidate(candidate.id, {}), 'От этого Candidate не удалось сделать ветку.')
                }
              >
                {`More like this · ${headline}`}
              </button>
              {!SINGLE_IMAGE.includes(assetType) ? null : (
                /* R085 names this affordance: `Adjust` opens the constrained bench. */
                <Link to={`/experiments/${experimentId}/refine/${candidate.id}/bench`} className="secondary-link">
                  {`Править ${headline}`}
                </Link>
              )}
              <Link to={`/experiments/${experimentId}/refine/${candidate.id}`} className="secondary-link">
                {candidate.items.length > 0 ? `Открыть набор ${headline}` : `Открыть ${headline}`}
              </Link>
            </>
          )}
          {!stopped ? null : (
            <button
              type="button"
              className="secondary"
              disabled={busy}
              onClick={() =>
                act(() => api.retrySlot(slot.id), 'Этот слот не удалось повторить.')
              }
            >
              {`Повторить ${headline}`}
            </button>
          )}
        </div>
        {failure === null ? null : <Failure message={failure} />}

        {slot.state === 'working' && candidate === null ? null : <SlotDetails slotId={slot.id} />}
      </div>
    </article>
  );
}

export const surface: Surface = {
  id: 'experiment-explore',
  path: '/experiments/:experimentId/explore',
  title: 'Explore',
  render: (params) => <Explore experimentId={params.experimentId ?? ''} />,
};
